import React from 'react';
import styled, {css} from 'styled-components';
import Button from '../../../shared/components/buttons/button/button';
import {CircularProgressbar} from 'react-circular-progressbar';
import {FormattedMessage, useIntl} from 'react-intl';
import {buildStyles} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {RetainerTooltips as Tooltips} from './RetainerTooltips';
import {HelpIcon} from '../../../../web-components/design-icons/HelpIcon.js';
import {WarningIcon} from '../../../../images/WarningIcon';
import {hasPermission} from '../../../shared/util/PermissionsUtil';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../../constants';
import Util from '../../../shared/util/util';
import {HOVER_CURSOR} from '../../../../web-components/design-icons/base-icon';

export const PeriodDataVisualsWrapper = styled.div`
	width: 100%;
	min-height: 128px;
	max-height: 128px;
	text-align: center;
	color: #ffffff;
	display: flex;
	flex-direction: row;
	margin-top: 1px;
`;

export const TimeRegChartContainer = styled.div`
	width: calc(100% - 340px - 174px);
	padding-top: 14px;
	flex-grow: 1;
	color: #535353;
	border: solid 1px #cecece;
`;

export const RadialProgressBarTile = styled.div`
	width: 174px;
	height: 128px;
	display: flex;
	flex-direction: column;
	gap: 5px;
	padding-top: 5px;
	padding-left: 5px;
	color: #535353;
	border: solid 1px #cecece;
	border-left: none;
`;

export const RadialProgressBarTileHeader = styled.div`
	font-size: 9px;
	text-transform: uppercase;
	letter-spacing: 1px;
	line-height: 14px;
	display: flex;
	gap: 5px;
`;

export const PeriodTileHeader = styled.div<{color?: string}>`
	font-size: 9px;
	text-transform: uppercase;
	letter-spacing: 1px;
	line-height: 14px;
	display: flex;
	gap: 5px;
	justify-content: flex-end;
	text-align: right;
	align-self: flex-end;
	width: 120px;
	color: ${({color}) => color};
`;

export const PeriodTilesGrid = styled.div`
	min-width: 340px;
	display: grid;
	grid-template-columns: 1fr 1fr;
`;

export const PeriodTile = styled.div<{
	overdue?: boolean;
	red?: boolean;
	click?: boolean;
	conflicts?: boolean;
	color?: string;
	withGap?: boolean;
	noUpperBorder?: boolean;
	noLowerBorder?: boolean;
	noLeftBorder?: boolean;
	noRightBorder?: boolean;
}>`
	height: 64px;
	width: 170px;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	padding: 5px 15px 0 0;
	gap: ${({withGap}) => withGap && '10px'};
	color: #535353;
	border: solid 1px #cecece;
	${({overdue}) =>
		overdue &&
		css`
			padding: 0;
			justify-content: center;

			align-items: center;
		`}
	${({red}) =>
		red &&
		css`
			background-color: #ec4b4b;
			color: #ffffff;
		`} 
	${({color}) =>
		color &&
		css`
			background-color: ${color};
			color: #000000;
			border: solid 1px #ffffff;
		`};
	${({noUpperBorder}) =>
		noUpperBorder &&
		css`
			border-top: none;
		`}
	${({noLowerBorder}) =>
		noLowerBorder &&
		css`
			border-bottom: none;
		`}
	${({noLeftBorder}) =>
		noLeftBorder &&
		css`
			border-left: none;
		`}
	${({noRightBorder}) =>
		noRightBorder &&
		css`
			border-right: none;
		`}
`;

export const CombinedNumber = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	gap: 10px;
	align-items: center;
`;

export const PeriodText = styled.div<{color?: string}>`
	font-size: 20px;
	font-weight: 500;
	display: flex;
	gap: 5px;
	justify-content: flex-end;
	color: ${({color}) => color};
`;

export const HoverContainer = styled.div`
	width: 150px;
	display: flex;
	flex-direction: column;
	background: #ffffff;
	gap: 15px;
	margin: 0 -15px;
`;

export const HoverLine = styled.div<{total?: boolean}>`
	display: flex;
	flex-direction: column;
	gap: 5px;
	width: 100%;
	${({total}) =>
		total &&
		css`
			border-top: solid 1px #cecece;
			padding-top: 15px;
		`}
`;

export const HoverLineHeader = styled.div`
	font-size: 9px;
	text-transform: uppercase;
	line-height: 1;
	letter-spacing: 1px;
	text-align: right;
	color: #a1a1a1;
	margin: 0 15px;
`;

export const HoverLineNumber = styled.div<{large?: boolean}>`
	font-size: ${({large}) => (large ? '22px' : '18px')};
	white-space: nowrap;
	text-align: right;
	margin: 0 17px;
`;

export const ButtonValueWrapper = styled.div<{conflicts?: boolean}>`
	display: flex;
	justify-content: space-between;
	margin-left: 15px;
	color: ${({conflicts}) => (conflicts ? '#fff' : 'inherit')};
`;

export const LockButton = styled(Button)`
	width: 140px;
	height: 36px;
`;

export const InfoIcon = styled.div`
	flex-grow: 0;
	width: 20px;
	height: 20px;
`;

export const StyledCircularProgressbar = styled(CircularProgressbar)`
	width: 60%;
`;

export const RadialProgressBarWrapper = styled.div`
	min-height: 100%;
	position: relative;
`;

interface RetainerPeriodRadialProgressBarProps {
	isFixedPrice: boolean;
	periodDifference: number;
	progressValue: number;
	projectColor: string;
}

export const RetainerPeriodRadialProgressBar: React.FC<RetainerPeriodRadialProgressBarProps> = ({
	isFixedPrice,
	periodDifference,
	progressValue,
	projectColor,
}) => {
	const progressPathColor = progressValue > 100 ? '#ec4b4b' : projectColor;
	const progressTrailColor = '#f3f3f3';
	const progressTextColor = '#535353';
	return (
		<RadialProgressBarTile>
			<RadialProgressBarTileHeader>
				<FormattedMessage
					id={isFixedPrice ? 'retainer.actual_percentage_budget_used' : 'retainer.actual_percentage_target_used'}
				/>
				{isFixedPrice ? (
					periodDifference ? (
						<Tooltips.ActualPercentageBudgetUsedWithRolloverTooltip>
							<HelpIcon size={'xs'} hoverCursor={HOVER_CURSOR.POINTER} />
						</Tooltips.ActualPercentageBudgetUsedWithRolloverTooltip>
					) : (
						<Tooltips.ActualPercentageBudgetUsedTooltip>
							<HelpIcon size={'xs'} hoverCursor={HOVER_CURSOR.POINTER} />
						</Tooltips.ActualPercentageBudgetUsedTooltip>
					)
				) : periodDifference ? (
					<Tooltips.ActualPercentageTargetUsedWithRolloverTooltip>
						<HelpIcon size={'xs'} hoverCursor={HOVER_CURSOR.POINTER} />
					</Tooltips.ActualPercentageTargetUsedWithRolloverTooltip>
				) : (
					<Tooltips.ActualPercentageTargetUsedTooltip>
						<HelpIcon size={'xs'} hoverCursor={HOVER_CURSOR.POINTER} />
					</Tooltips.ActualPercentageTargetUsedTooltip>
				)}
			</RadialProgressBarTileHeader>
			<RadialProgressBarWrapper>
				<StyledCircularProgressbar
					styles={buildStyles({
						// Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
						strokeLinecap: 'butt',
						textSize: '16px',
						pathTransition: 'stroke-dashoffset 0.5s ease 0s',
						// How long animation takes to go from one percentage to another, in seconds
						pathTransitionDuration: 0.5,
						pathColor: progressPathColor,
						trailColor: progressTrailColor,
						textColor: progressTextColor,
					})}
					value={progressValue}
					text={`${progressValue}%`}
				/>
			</RadialProgressBarWrapper>
		</RadialProgressBarTile>
	);
};

interface PeriodTargetTileProps {
	isFixedPrice: boolean;
	periodDifference: number;
	periodTarget: number;
	periodAmount: number;
	withSuffix: (value: number) => string;
	/**
	 * False when the viewer may not read the period's price fields. Every amount this tile shows is
	 * derived from them, and they arrive as null rather than absent, so formatting one would print a
	 * currency-formatted zero. The tile then keeps its place in the grid with no number at all.
	 */
	canReadAmount: boolean;
}

export const PeriodTargetTile: React.FC<PeriodTargetTileProps> = ({
	isFixedPrice,
	periodDifference,
	periodTarget,
	periodAmount,
	canReadAmount,
	withSuffix,
}) => {
	return (
		<PeriodTile data-cy={'period-target-tile'} withGap noLeftBorder>
			<PeriodTileHeader>
				{isFixedPrice ? (
					periodDifference ? (
						<FormattedMessage id={'retainer.adjusted_price'} />
					) : (
						<FormattedMessage id={'retainer.period_price'} />
					)
				) : periodDifference ? (
					<FormattedMessage id={'retainer.adjusted_target'} />
				) : (
					<FormattedMessage id={'retainer.period_target'} />
				)}
				{!!periodDifference && (
					<Tooltips.PeriodBreakdownTooltip
						isFixedPrice={isFixedPrice}
						periodTarget={periodTarget}
						periodDifference={periodDifference}
						withSuffix={withSuffix}
					>
						<HelpIcon size={'xs'} hoverCursor={HOVER_CURSOR.POINTER} />
					</Tooltips.PeriodBreakdownTooltip>
				)}
			</PeriodTileHeader>
			{!canReadAmount ? null : periodDifference ? (
				<CombinedNumber>
					<InfoIcon>
						<WarningIcon />
					</InfoIcon>
					<PeriodText>{withSuffix(periodTarget + periodDifference)}</PeriodText>
				</CombinedNumber>
			) : (
				<PeriodText>{withSuffix(periodAmount)}</PeriodText>
			)}
		</PeriodTile>
	);
};

interface PeriodActualTileProps {
	isFixedPrice: boolean;
	actualHours: number;
	actualPrice: number;
	withSuffix: (value: number) => string;
	projectColor: string;
	/**
	 * False when the viewer may not read the period's price fields. The billable actual amount arrives
	 * as null rather than absent, so formatting it would print a currency-formatted zero. The tile then
	 * keeps its place in the grid with no number at all.
	 */
	canReadAmount: boolean;
}

export const PeriodActualTile: React.FC<PeriodActualTileProps> = ({
	isFixedPrice,
	actualHours,
	actualPrice,
	canReadAmount,
	withSuffix,
	projectColor,
}) => {
	return (
		<PeriodTile color={projectColor} noLeftBorder noUpperBorder>
			<PeriodTileHeader color={Util.getTextColorV2(projectColor)}>
				<FormattedMessage
					id={isFixedPrice ? 'common.actual_billable_value_of_service' : 'common.actual_billable_hours'}
				/>
			</PeriodTileHeader>
			{!canReadAmount ? null : (
				<PeriodText color={Util.getTextColorV2(projectColor)}>
					{withSuffix(isFixedPrice ? actualPrice : actualHours)}
				</PeriodText>
			)}
		</PeriodTile>
	);
};

interface PeriodConflictsTileProps {
	totalConflicted: number;
	withSuffix: (value: number) => string;
	onFixConflicts: () => void;
}

const FixButton = styled(Button)`
	&.custom-button-container.outline.red {
		color: #ffffff;
		border-color: #ffffff;
	}
`;

export const PeriodConflictsTile: React.FC<PeriodConflictsTileProps> = ({totalConflicted, withSuffix, onFixConflicts}) => {
	const {formatMessage} = useIntl();
	return (
		<PeriodTile red withGap noLeftBorder noUpperBorder>
			<PeriodTileHeader>{formatMessage({id: 'retainer.time_entry_conflicts_short'})}</PeriodTileHeader>
			<ButtonValueWrapper>
				<Tooltips.NoTimeRegUpdatePermissionTooltip>
					<FixButton
						buttonStyle={BUTTON_STYLE.OUTLINE}
						colorTheme={BUTTON_COLOR.RED}
						text={formatMessage({id: 'retainer.fix_conflicts'})}
						onClick={onFixConflicts}
						isDisabled={!hasPermission('TIME_REGISTRATION_UPDATE_ALL')}
						cy={'fix-conflict-button'}
					/>
				</Tooltips.NoTimeRegUpdatePermissionTooltip>
				{withSuffix(totalConflicted)}
			</ButtonValueWrapper>
		</PeriodTile>
	);
};

interface PeriodLockTileProps {
	useTimeApproval: boolean;
	onLockPeriod: () => void;
}

export const PeriodLockTile: React.FC<PeriodLockTileProps> = ({useTimeApproval, onLockPeriod}) => {
	const {formatMessage} = useIntl();
	return (
		<PeriodTile overdue noLeftBorder noUpperBorder>
			<LockButton
				buttonStyle={BUTTON_STYLE.FILLED}
				colorTheme={BUTTON_COLOR.GREEN}
				text={formatMessage({
					id: useTimeApproval ? 'retainer.approve_lock_period' : 'retainer.lock_period',
				})}
				onClick={onLockPeriod}
			/>
		</PeriodTile>
	);
};

export const PeriodEmptyTile: React.FC = () => {
	return <PeriodTile tabIndex={0} noLeftBorder noUpperBorder />;
};

interface PeriodRemainingTileProps {
	isTimeMaterials: boolean;
	isFixedPrice: boolean;
	inHours: boolean;
	actualHours: number;
	actualPrice: number;
	periodTargetTotal: number;
	currency: string;
	withSuffix: (value: number) => string;
	projectColor: string;
	/**
	 * False when the viewer may not read the price fields this tile spends. Both branches arrive as
	 * null rather than absent, so formatting one would print a currency-formatted zero. The tile then
	 * keeps its place in the grid with no number at all. Note the time-and-materials branch is a
	 * currency amount even on an otherwise hours-based period, so the caller decides per branch.
	 */
	canReadAmount: boolean;
}

export const PeriodRemainingTile: React.FC<PeriodRemainingTileProps> = ({
	isTimeMaterials,
	isFixedPrice,
	inHours,
	actualHours,
	actualPrice,
	periodTargetTotal,
	canReadAmount,
	currency,
	withSuffix,
	projectColor,
}) => {
	const {formatNumber} = useIntl();
	return (
		<PeriodTile color={projectColor} noLeftBorder noUpperBorder noLowerBorder>
			{isTimeMaterials ? (
				<>
					<PeriodTileHeader color={Util.getTextColorV2(projectColor)}>
						<FormattedMessage id={'common.actual_billable_value_of_service'} />
					</PeriodTileHeader>
					{!canReadAmount ? null : (
						<PeriodText color={Util.getTextColorV2(projectColor)}>
							{Util.getFormattedCurrencyValue(
								Util.GetCurrencySymbol(currency),
								formatNumber(actualPrice, {format: 'always_two_decimal'})
							)}
						</PeriodText>
					)}
				</>
			) : (
				<>
					<PeriodTileHeader color={Util.getTextColorV2(projectColor)}>
						<FormattedMessage
							id={isFixedPrice ? 'retainer.actual_budget_remaining' : 'retainer.actual_budget_hours_remaining'}
						/>
					</PeriodTileHeader>
					{!canReadAmount ? null : (
						<PeriodText color={Util.getTextColorV2(projectColor)}>
							{withSuffix(Math.max(0, periodTargetTotal - (inHours ? actualHours : actualPrice)))}
						</PeriodText>
					)}
				</>
			)}
		</PeriodTile>
	);
};
